"use client"

import { Card, CardContent } from "@/components/ui/card"
import { Loader2 } from "lucide-react"
import { HeroSection } from "./hero-section"
import { HeroSocialProof } from "./hero-social-proof"
import { HomeHeroShell } from "./home-hero-shell"

// Renders the live tree's *target* state (post-entrance) as plain DOM. The
// live tree fades in from `opacity: 0, y: -8`; rendering the fallback at the
// final position means the SSR → CSR swap has no visible jump. Card className
// must stay in sync with `app/page-content.tsx:#analysis-card`.
//
// IMPORTANT: do NOT render UploadCard / SamplePhotos here. Doing so causes
// /perfect.JPEG and /avoid.JPEG to be fetched twice — once by the SSR fallback
// and once by the live tree after hydration — because both <img> tags are
// inflight before the browser cache populates. Instead reserve the same
// vertical footprint with neutral placeholder blocks; the swap stays jump-free.
export function HomeHeroFallback({
  socialProofCountLabel = null,
}: {
  socialProofCountLabel?: string | null
}) {
  return (
    <HomeHeroShell>
      <HeroSection mode="identify" />
      {/* Mirror the live tree's social-proof row (same min-height wrapper, no
          motion) so the scan count lands in the statically-prerendered HTML
          and the SSR → CSR swap stays jump-free. */}
      <div className="min-h-[44px]">
        {/* variant must match the hydrated tree's value in app/page-content.tsx
            or hero_social_proof_viewed attribution splits between the SSR
            fallback and the hydrated impression. */}
        <HeroSocialProof
          variant="verified_count_v3"
          countLabel={socialProofCountLabel}
        />
      </div>
      <Card
        id="analysis-card"
        aria-busy="true"
        aria-describedby="home-tools-loading-status"
        className="bg-white/95 shadow-[0_12px_40px_rgb(0,0,0,0.04)] border border-white/60 rounded-2xl overflow-hidden"
      >
        <CardContent className="p-6 md:p-10">
          <div className="text-center">
            <p className="text-base font-medium text-gray-500 mb-4">
              Snap a close-up of your lawn
            </p>
            {/* Sample-photo placeholders — match SamplePhotos grid dimensions
                so the live tree mounts without CLS. No <Image> here. */}
            <div className="grid grid-cols-2 gap-4 md:gap-6 mb-5 max-w-xl mx-auto">
              <div className="rounded-2xl bg-gray-100 aspect-4/3" aria-hidden="true" />
              <div className="rounded-2xl bg-gray-100 aspect-4/3" aria-hidden="true" />
            </div>
            <div className="max-w-md mx-auto">
              <div
                id="home-tools-loading-status"
                role="status"
                className="flex w-full items-center justify-center gap-3 rounded-lg bg-green-700 text-white font-semibold text-lg py-6 shadow-lg shadow-green-600/20"
              >
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                Getting photo tools ready
              </div>
              <div className="mt-6 text-sm md:text-base text-gray-600">
                Upload and diagnosis controls will be ready in a moment.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      <div className="h-10" aria-hidden="true" />
    </HomeHeroShell>
  )
}
